import { FC } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Skeleton, Typography } from '@mui/material'

import { GAME_ICON_RADIUS, gameIconSx } from '../../helpers/styles'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { TermRef } from '../../types'

interface GamePageIdentityProps {
  /** GameRefinery game id; the name links to the GameRefinery game page only when set. */
  grGameId?: string
  name?: string
  icon?: string
  genre?: TermRef
  /** Compact (scrolled) state: shrinks the icon, drops the title variant, hides the genre. */
  compact: boolean
  /**
   * `true` only during the very first fetch. Renders a layout-matched
   * skeleton rather than an empty title — the page previously looked broken
   * between navigation and the first response.
   */
  isLoading: boolean
}

/**
 * Game identity block for the UA Insights game page sticky header: icon, name
 * and (when expanded) genre. The icon doubles as a visual anchor so the user
 * immediately recognises which game they are looking at.
 */
export const GamePageIdentity: FC<GamePageIdentityProps> = ({ grGameId, name, icon, genre, compact, isLoading }) => {
  const { genreLabel } = useUATaxonomy()

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 1.5, flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 1.5 }}>
          <Skeleton variant="rounded" width={compact ? 26 : 38} height={compact ? 26 : 38} sx={{ borderRadius: GAME_ICON_RADIUS, flexShrink: 0 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Skeleton variant="text" width={compact ? 160 : 220} height={compact ? 24 : 34} />
            {!compact && <Skeleton variant="text" width={120} height={14} />}
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 1.5, flex: 1, minWidth: 0 }}>
      {icon && (
        <Box
          component="img"
          src={icon}
          alt={name}
          sx={{
            ...gameIconSx(compact ? 26 : 38),
            boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.15)',
            transition: 'width 160ms ease, height 160ms ease',
          }}
        />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <Typography
          variant={compact ? 'h6' : 'h2'}
          component="h1"
          sx={{
            fontWeight: 700,
            minWidth: 0,
            // Constrain to a single line so a long game name shortens with an
            // ellipsis instead of pushing the sticky header taller.
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {grGameId ? (
            <Box
              component={RouterLink}
              to={`/game/${grGameId}`}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {name ?? ''}
            </Box>
          ) : (
            name ?? ''
          )}
        </Typography>
        {!compact && genre && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: 12,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {genreLabel(genre)}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
